import React, { useEffect, useRef, useState } from "react";
import "./AudioVisualizerStyles.css";

interface StereoAudioVisualizerProps {
  audioUrl: string;
  isPlaying: boolean;
  onEnded?: () => void;
}

/**
 * A stereo audio visualizer component that displays horizontal waveforms
 * for agent and user channels with the full audio clip visible
 */
const StereoAudioVisualizer: React.FC<StereoAudioVisualizerProps> = ({
  audioUrl,
  isPlaying,
  onEnded,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const agentCanvasRef = useRef<HTMLCanvasElement>(null);
  const userCanvasRef = useRef<HTMLCanvasElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<number>();
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const splitterRef = useRef<ChannelSplitterNode | null>(null);
  const leftAnalyserRef = useRef<AnalyserNode | null>(null);
  const rightAnalyserRef = useRef<AnalyserNode | null>(null);
  const leftGainRef = useRef<GainNode | null>(null);
  const rightGainRef = useRef<GainNode | null>(null);
  const mergerRef = useRef<ChannelMergerNode | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const audioInitializedRef = useRef<boolean>(false);

  // Full waveform data to display
  const agentWaveformDataRef = useRef<number[]>([]);
  const userWaveformDataRef = useRef<number[]>([]);
  const playbackPositionRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [audioReady, setAudioReady] = useState<boolean>(false);
  const [isAgentActive, setIsAgentActive] = useState<boolean>(false);
  const [isUserActive, setIsUserActive] = useState<boolean>(false);
  const [agentMuted, setAgentMuted] = useState<boolean>(false);
  const [userMuted, setUserMuted] = useState<boolean>(false);
  const [audioLoaded, setAudioLoaded] = useState<boolean>(false);

  // Helper function to get computed CSS variable value
  const getCssVariable = (variableName: string): string => {
    if (typeof window === "undefined") return "#10b981"; // Default fallback color
    const value = getComputedStyle(document.documentElement).getPropertyValue(
      variableName.replace("var(", "").replace(")", "").trim()
    );
    return value || "#10b981"; // Fallback to a default color if variable not found
  };

  // Set up audio and visualization
  useEffect(() => {
    if (!audioUrl) return;

    // Clean up any existing audio nodes first
    const cleanupAudioNodes = () => {
      if (sourceRef.current) sourceRef.current.disconnect();
      if (splitterRef.current) splitterRef.current.disconnect();
      if (leftAnalyserRef.current) leftAnalyserRef.current.disconnect();
      if (rightAnalyserRef.current) rightAnalyserRef.current.disconnect();
      if (leftGainRef.current) leftGainRef.current.disconnect();
      if (rightGainRef.current) rightGainRef.current.disconnect();
      if (mergerRef.current) mergerRef.current.disconnect();

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // Reset references
      sourceRef.current = null;
      splitterRef.current = null;
      leftAnalyserRef.current = null;
      rightAnalyserRef.current = null;
      leftGainRef.current = null;
      rightGainRef.current = null;
      mergerRef.current = null;
    };

    // Only initialize audio if it hasn't been initialized yet
    if (!audioInitializedRef.current) {
      // Clean up existing audio
      cleanupAudioNodes();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }

      // Reset waveform data
      agentWaveformDataRef.current = [];
      userWaveformDataRef.current = [];
      setAudioLoaded(false);
      playbackPositionRef.current = 0;

      // Create new audio element
      const audio = new Audio();
      audioRef.current = audio;

      // Set the src
      audio.src = audioUrl;
      audio.preload = "metadata";

      // Generate full waveform data by analyzing the entire audio
      const generateFullWaveformData = async () => {
        // Create a separate context just for waveform generation
        const waveformContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();

        try {
          // Fetch audio data
          const response = await fetch(audioUrl);
          const arrayBuffer = await response.arrayBuffer();

          // Decode audio data
          const audioBuffer = await waveformContext.decodeAudioData(
            arrayBuffer
          );

          // Get audio data for each channel
          const leftChannel = audioBuffer.getChannelData(0);
          const rightChannel =
            audioBuffer.numberOfChannels > 1
              ? audioBuffer.getChannelData(1)
              : audioBuffer.getChannelData(0);

          // Downsample for visualization
          const dataPoints = 1000; // Increased for more detailed waveform
          agentWaveformDataRef.current = downsampleAudioData(
            leftChannel,
            dataPoints
          );
          userWaveformDataRef.current = downsampleAudioData(
            rightChannel,
            dataPoints
          );

          setAudioLoaded(true);

          // Draw initial waveforms
          requestAnimationFrame(() => {
            drawWaveforms();
          });
        } catch (error) {
          console.error("Error generating waveform data:", error);
        } finally {
          waveformContext.close();
        }
      };

      const setupAudio = () => {
        setAudioReady(true);
        setDuration(audio.duration);

        // Create fresh audio context and nodes
        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        audioContextRef.current = audioContext;

        const source = audioContext.createMediaElementSource(audio);
        sourceRef.current = source;

        // Create stereo channel splitter
        const splitter = audioContext.createChannelSplitter(2);
        splitterRef.current = splitter;

        // Create gain nodes for muting
        const leftGain = audioContext.createGain();
        leftGainRef.current = leftGain;

        const rightGain = audioContext.createGain();
        rightGainRef.current = rightGain;

        // Create merger for mono output
        const merger = audioContext.createChannelMerger(1);
        mergerRef.current = merger;

        // Create analyzers for left and right channels
        const leftAnalyser = audioContext.createAnalyser();
        leftAnalyser.fftSize = 2048; // Higher for more detailed waveform
        leftAnalyser.smoothingTimeConstant = 0.3;
        leftAnalyserRef.current = leftAnalyser;

        const rightAnalyser = audioContext.createAnalyser();
        rightAnalyser.fftSize = 2048;
        rightAnalyser.smoothingTimeConstant = 0.3;
        rightAnalyserRef.current = rightAnalyser;

        // Connect the nodes:
        // source -> splitter -> [leftGain/rightGain] -> [leftAnalyser/rightAnalyser] -> merger -> destination
        source.connect(splitter);

        splitter.connect(leftGain, 0); // Connect left channel to left gain
        splitter.connect(rightGain, 1); // Connect right channel to right gain

        leftGain.connect(leftAnalyser);
        rightGain.connect(rightAnalyser);

        // Mono output: mix both channels to a single channel
        leftGain.connect(merger, 0, 0);
        rightGain.connect(merger, 0, 0);

        // Connect to destination for audio output
        merger.connect(audioContext.destination);

        // Mark as initialized
        audioInitializedRef.current = true;

        // Generate full waveform data for visualization
        generateFullWaveformData();
      };

      // Direct update of slider on time change
      const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        setCurrentTime(audioRef.current.currentTime);

        // Check channel activity
        if (leftAnalyserRef.current && rightAnalyserRef.current) {
          const agentData = new Uint8Array(
            leftAnalyserRef.current.frequencyBinCount
          );
          const userData = new Uint8Array(
            rightAnalyserRef.current.frequencyBinCount
          );

          leftAnalyserRef.current.getByteFrequencyData(agentData);
          rightAnalyserRef.current.getByteFrequencyData(userData);

          setIsAgentActive(calculateVolume(agentData) > 15);
          setIsUserActive(calculateVolume(userData) > 15);
        }
      };

      audio.addEventListener("loadedmetadata", setupAudio);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("ended", () => {
        if (onEnded) onEnded();
      });
    }

    return () => {
      cancelAnimationFrame(requestRef.current!);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener("loadedmetadata", () => {});
        audioRef.current.removeEventListener("timeupdate", () => {});
        audioRef.current.removeEventListener("ended", () => {});
      }

      // Don't clean up audio nodes when unmounting - only when changing audio sources
      // This prevents the audio from being reset on pause/play actions
    };
  }, [audioUrl, onEnded]);

  // Effect to ensure animation starts immediately when isPlaying changes
  useEffect(() => {
    if (isPlaying && audioRef.current && audioReady && audioLoaded) {
      // Start animation immediately without waiting for play to complete
      cancelAnimationFrame(requestRef.current!);
      requestRef.current = requestAnimationFrame(animatePlayback);
    }
  }, [isPlaying, audioReady, audioLoaded]);

  // Handle play/pause without audio reinitializing
  useEffect(() => {
    if (!audioRef.current || !audioReady) return;

    // Store current position for transitions
    if (audioRef.current && audioRef.current.duration) {
      playbackPositionRef.current =
        audioRef.current.currentTime / audioRef.current.duration;
    }

    const playAudio = async () => {
      try {
        if (
          audioContextRef.current &&
          audioContextRef.current.state === "suspended"
        ) {
          await audioContextRef.current.resume();
        }

        // Play without reinitializing
        await audioRef.current!.play();

        // Start animation loop
        requestRef.current = requestAnimationFrame(animatePlayback);
      } catch (err) {
        console.error("Error playing audio:", err);
      }
    };

    if (isPlaying) {
      playAudio();
    } else {
      // Just pause without reinitializing
      audioRef.current.pause();

      // Cancel the animation frame
      cancelAnimationFrame(requestRef.current!);

      // Final render to keep the position indicator visible
      drawWaveforms();
    }
  }, [isPlaying, audioReady]);

  // Handle mute/unmute for agent channel
  useEffect(() => {
    if (!leftGainRef.current || !audioContextRef.current) return;
    const gainNode = leftGainRef.current;
    const audioContext = audioContextRef.current;

    // Get current time from audio context for precise scheduling
    const now = audioContext.currentTime;

    // Use exponentialRamp for smoother, faster-feeling transition
    // with a very short ramp time (0.01 seconds)
    gainNode.gain.cancelScheduledValues(now);
    gainNode.gain.setValueAtTime(gainNode.gain.value, now);

    // Can't ramp to exactly 0, so use a very small value if muted
    gainNode.gain.exponentialRampToValueAtTime(
      agentMuted ? 0.0001 : 1,
      now + 0.01
    );

    // If fully muted, set to 0 after the ramp completes
    if (agentMuted) {
      gainNode.gain.setValueAtTime(0, now + 0.01);
    }
  }, [agentMuted]);

  // Handle mute/unmute for user channel
  useEffect(() => {
    if (!rightGainRef.current || !audioContextRef.current) return;
    const gainNode = rightGainRef.current;
    const audioContext = audioContextRef.current;

    // Get current time from audio context for precise scheduling
    const now = audioContext.currentTime;

    // Use exponentialRamp for smoother, faster-feeling transition
    // with a very short ramp time (0.01 seconds)
    gainNode.gain.cancelScheduledValues(now);
    gainNode.gain.setValueAtTime(gainNode.gain.value, now);

    // Can't ramp to exactly 0, so use a very small value if muted
    gainNode.gain.exponentialRampToValueAtTime(
      userMuted ? 0.0001 : 1,
      now + 0.01
    );

    // If fully muted, set to 0 after the ramp completes
    if (userMuted) {
      gainNode.gain.setValueAtTime(0, now + 0.01);
    }
  }, [userMuted]);

  // Add global mouse event listeners for slider dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !sliderRef.current || !audioRef.current)
        return;

      const rect = sliderRef.current.getBoundingClientRect();
      let position = (e.clientX - rect.left) / rect.width;

      // Clamp the position between 0 and 1
      position = Math.min(Math.max(position, 0), 1);

      const newTime = position * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      playbackPositionRef.current = position;

      // Force immediate update of slider without waiting for timeupdate event
      const progress = position * 100;
      if (progressRef.current) {
        progressRef.current.style.width = `${progress}%`;
      }

      setCurrentTime(newTime);

      // Force redraw of track position indicators even when paused
      requestAnimationFrame(() => {
        // Force redraws of individual canvases
        if (agentCanvasRef.current && userCanvasRef.current && audioLoaded) {
          // Get accent color from CSS variables
          const accentColor = getCssVariable("--accent");

          // Force redraw of both canvases with the updated position
          drawWaveform(
            agentCanvasRef.current,
            agentWaveformDataRef.current,
            accentColor,
            playbackPositionRef.current
          );

          drawWaveform(
            userCanvasRef.current,
            userWaveformDataRef.current,
            accentColor,
            playbackPositionRef.current
          );
        }
      });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // Downsample audio data to a specific number of points
  const downsampleAudioData = (
    channelData: Float32Array,
    targetPoints: number
  ): number[] => {
    const result: number[] = [];
    const blockSize = Math.floor(channelData.length / targetPoints);

    for (let i = 0; i < targetPoints; i++) {
      const start = i * blockSize;
      let sum = 0;
      let count = 0;

      // Get average value for this block
      for (let j = 0; j < blockSize && start + j < channelData.length; j++) {
        sum += Math.abs(channelData[start + j]);
        count++;
      }

      const average = count > 0 ? sum / count : 0;
      // Scale to 0-100 range for easier visualization
      result.push(average * 100);
    }

    return result;
  };

  // Update slider progress - this is a direct function now
  const updateSliderProgress = () => {
    if (!progressRef.current || !audioRef.current || !audioRef.current.duration)
      return;

    const progress =
      (audioRef.current.currentTime / audioRef.current.duration) * 100;
    progressRef.current.style.width = `${progress}%`;
  };

  // Handle click and drag on time slider
  const handleSliderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!sliderRef.current || !audioRef.current || !audioRef.current.duration)
      return;

    // Start dragging
    isDraggingRef.current = true;

    const rect = sliderRef.current.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    const newTime = clickPosition * audioRef.current.duration;

    // Store the new time
    audioRef.current.currentTime = newTime;
    playbackPositionRef.current = clickPosition;
    setCurrentTime(newTime);

    // Force immediate update of slider
    if (progressRef.current) {
      const progress = (newTime / audioRef.current.duration) * 100;
      progressRef.current.style.width = `${progress}%`;
    }

    // Force immediate redraw of the waveforms
    // This ensures the track position indicators update even when paused
    requestAnimationFrame(() => {
      // Force redraws of individual canvases
      if (agentCanvasRef.current && userCanvasRef.current && audioLoaded) {
        // Get accent color from CSS variables
        const accentColor = getCssVariable("--accent");

        // Force redraw of both canvases with the updated position
        drawWaveform(
          agentCanvasRef.current,
          agentWaveformDataRef.current,
          accentColor,
          playbackPositionRef.current
        );

        drawWaveform(
          userCanvasRef.current,
          userWaveformDataRef.current,
          accentColor,
          playbackPositionRef.current
        );
      }
    });

    // If playing, ensure animation continues
    if (isPlaying) {
      cancelAnimationFrame(requestRef.current!);
      requestRef.current = requestAnimationFrame(animatePlayback);
    }
  };

  // Handle mouse down on slider
  const handleSliderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isDraggingRef.current = true;
    handleSliderClick(e);
  };

  // Draw waveforms for both channels
  const drawWaveforms = () => {
    if (!agentCanvasRef.current || !userCanvasRef.current || !audioLoaded)
      return;

    // Set canvas dimensions with high DPR for sharper rendering
    const setCanvasDimensions = (canvas: HTMLCanvasElement) => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
    };

    setCanvasDimensions(agentCanvasRef.current);
    setCanvasDimensions(userCanvasRef.current);

    // Get accent color from CSS variables
    const accentColor = getCssVariable("--accent");

    drawWaveform(
      agentCanvasRef.current,
      agentWaveformDataRef.current,
      accentColor,
      playbackPositionRef.current
    );

    drawWaveform(
      userCanvasRef.current,
      userWaveformDataRef.current,
      accentColor,
      playbackPositionRef.current
    );
  };

  // Draw a single waveform on a canvas
  const drawWaveform = (
    canvas: HTMLCanvasElement,
    data: number[],
    color: string,
    position: number
  ) => {
    const ctx = canvas.getContext("2d")!;
    const width = canvas.width;
    const height = canvas.height;
    const dpr = window.devicePixelRatio || 1;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = getCssVariable("--card-container");
    ctx.fillRect(0, 0, width, height);

    if (data.length === 0) return;

    // Draw the waveform
    const barWidth = width / data.length;
    const centerY = height / 2;
    // Make waveforms much larger by decreasing the denominator
    const scaleFactor = height / 0.9;

    // Draw waveform
    ctx.beginPath();

    // Draw top half of waveform with more pronounced amplitude
    for (let i = 0; i < data.length; i++) {
      const x = i * barWidth;
      // Greatly amplify waveform values
      const amplitude = Math.max(0.01, data[i] * 0.025);
      const barHeight = amplitude * scaleFactor;

      // Draw above centerline
      const y1 = centerY - barHeight / 2;

      if (i === 0) {
        ctx.moveTo(x, y1);
      } else {
        ctx.lineTo(x, y1);
      }
    }

    // Complete the waveform by drawing bottom half in reverse order
    for (let i = data.length - 1; i >= 0; i--) {
      const x = i * barWidth;
      // Greatly amplify waveform values
      const amplitude = Math.max(0.01, data[i] * 0.025);
      const barHeight = amplitude * scaleFactor;

      // Draw below centerline
      const y2 = centerY + barHeight / 2;
      ctx.lineTo(x, y2);
    }

    ctx.closePath();

    // Fill with gradient using computed color values
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, color);
    gradient.addColorStop(0.5, color);
    gradient.addColorStop(1, color);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw playback position indicator with anti-aliasing for smoother line
    const positionX = Math.round(position * width) + 0.5;
    ctx.beginPath();
    ctx.moveTo(positionX, 0);
    ctx.lineTo(positionX, height);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2 * dpr;
    ctx.stroke();

    // Add glow effect to the position indicator
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = 10 * dpr;
    ctx.beginPath();
    ctx.moveTo(positionX, 0);
    ctx.lineTo(positionX, height);
    ctx.stroke();
    ctx.restore();
  };

  // Calculate volume from audio data
  const calculateVolume = (dataArray: Uint8Array): number => {
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    return sum / dataArray.length;
  };

  // Format time for display (mm:ss)
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Toggle mute for agent track
  const toggleAgentMute = () => {
    setAgentMuted(!agentMuted);
  };

  // Toggle mute for user track
  const toggleUserMute = () => {
    setUserMuted(!userMuted);
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      drawWaveforms();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Initial canvas setup
  useEffect(() => {
    if (!agentCanvasRef.current || !userCanvasRef.current) return;

    const agentCanvas = agentCanvasRef.current;
    const userCanvas = userCanvasRef.current;

    // Set initial dimensions
    const setInitialDimensions = (canvas: HTMLCanvasElement) => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
    };

    setInitialDimensions(agentCanvas);
    setInitialDimensions(userCanvas);

    // Initial draw
    drawWaveforms();
  }, [audioLoaded]);

  // Ensure we're actually using device pixel ratio correctly
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (agentCanvasRef.current && userCanvasRef.current) {
        const resizeCanvas = (canvas: HTMLCanvasElement) => {
          const dpr = window.devicePixelRatio || 1;
          const rect = canvas.getBoundingClientRect();
          canvas.width = rect.width * dpr;
          canvas.height = rect.height * dpr;
        };

        resizeCanvas(agentCanvasRef.current);
        resizeCanvas(userCanvasRef.current);
        drawWaveforms();
      }
    });

    if (agentCanvasRef.current) {
      resizeObserver.observe(agentCanvasRef.current);
    }

    if (userCanvasRef.current) {
      resizeObserver.observe(userCanvasRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [audioLoaded]);

  // Fix initial position tracking
  useEffect(() => {
    // Force initial draw once audio is loaded
    if (audioLoaded && audioRef.current) {
      // Wait for next frame to ensure everything is properly initialized
      requestAnimationFrame(() => {
        // Make sure we maintain position on load if it exists
        if (audioRef.current && audioRef.current.duration) {
          playbackPositionRef.current =
            audioRef.current.currentTime / audioRef.current.duration;
        }
        updateSliderProgress();
        drawWaveforms();

        // Start animation immediately if playing
        if (isPlaying) {
          requestRef.current = requestAnimationFrame(animatePlayback);
        }
      });
    }
  }, [audioLoaded, isPlaying]);

  // Initial draw on component mount to ensure waveforms are visible right away
  useEffect(() => {
    // Force position updates on play/pause to ensure slider synchronization
    if (audioRef.current) {
      updateSliderProgress();
      drawWaveforms();
    }
  }, [isPlaying]);

  // Reset when changing audio sources
  useEffect(() => {
    return () => {
      // Reset initialization flag when component is unmounted or audio URL changes
      audioInitializedRef.current = false;
    };
  }, [audioUrl]);

  // Add a smooth animation loop for playback visualization
  const animatePlayback = () => {
    if (!audioRef.current) return;

    // Update playback position with current time
    if (audioRef.current.duration) {
      // Only update position if we're playing
      if (!audioRef.current.paused) {
        const currentPosition =
          audioRef.current.currentTime / audioRef.current.duration;
        playbackPositionRef.current = currentPosition;
      }

      // Update slider with smooth animation
      if (progressRef.current) {
        const progress = playbackPositionRef.current * 100;
        progressRef.current.style.width = `${progress}%`;
      }

      // Update current time display
      setCurrentTime(audioRef.current.currentTime);

      // Update waveforms
      drawWaveforms();
    }

    // Continue animation loop only if still playing
    if (isPlaying && audioRef.current && !audioRef.current.paused) {
      requestRef.current = requestAnimationFrame(animatePlayback);
    }
  };

  return (
    <div className="audio-visualizer-container" ref={containerRef}>
      <div className="visualizer-tracks">
        <div
          className={`agent-track ${isAgentActive ? "active" : ""} ${
            agentMuted ? "muted-track" : ""
          }`}
          style={{ backgroundColor: "var(--card-container)" }}
        >
          <div className="track-header">
            <div className="track-label">
              <div
                className="radio-button"
                style={{ borderColor: "var(--border)" }}
              >
                <span
                  className="radio-dot"
                  style={{ backgroundColor: "var(--accent)" }}
                ></span>
              </div>
              <span className="text-[var(--foreground)]">Agent</span>
            </div>
            <button
              className={`mute-button ${agentMuted ? "muted" : ""}`}
              onClick={toggleAgentMute}
              aria-label={
                agentMuted ? "Unmute agent track" : "Mute agent track"
              }
              style={{
                backgroundColor: agentMuted ? "var(--muted)" : "transparent",
                color: "var(--foreground)",
                border: "1px solid var(--border)",
              }}
            >
              {agentMuted ? "Unmute" : "Mute"}
            </button>
          </div>
          <div className="canvas-container">
            <canvas ref={agentCanvasRef} className="track-canvas" />
            {agentMuted && (
              <div
                className="muted-overlay"
                style={{
                  backgroundColor: "var(--card-container)",
                  color: "var(--foreground)",
                }}
              >
                Muted
              </div>
            )}
          </div>
        </div>

        <div
          className={`user-track ${isUserActive ? "active" : ""} ${
            userMuted ? "muted-track" : ""
          }`}
          style={{ backgroundColor: "var(--card-container)" }}
        >
          <div className="track-header">
            <div className="track-label">
              <div
                className="radio-button"
                style={{ borderColor: "var(--border)" }}
              >
                <span
                  className="radio-dot"
                  style={{ backgroundColor: "var(--accent)" }}
                ></span>
              </div>
              <span className="text-[var(--foreground)]">User</span>
            </div>
            <button
              className={`mute-button ${userMuted ? "muted" : ""}`}
              onClick={toggleUserMute}
              aria-label={userMuted ? "Unmute user track" : "Mute user track"}
              style={{
                backgroundColor: userMuted ? "var(--muted)" : "transparent",
                color: "var(--foreground)",
                border: "1px solid var(--border)",
              }}
            >
              {userMuted ? "Unmute" : "Mute"}
            </button>
          </div>
          <div className="canvas-container">
            <canvas ref={userCanvasRef} className="track-canvas" />
            {userMuted && (
              <div
                className="muted-overlay"
                style={{
                  backgroundColor: "var(--card-container)",
                  color: "var(--foreground)",
                }}
              >
                Muted
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="time-progress">
        <div
          className="time-slider no-select"
          ref={sliderRef}
          onMouseDown={handleSliderMouseDown}
          style={{
            cursor: "pointer",
            backgroundColor: "var(--card-container)",
          }}
        >
          <div
            className="time-progress-bar"
            ref={progressRef}
            style={{ backgroundColor: "var(--accent)" }}
          ></div>
        </div>

        <div className="time-labels" style={{ color: "var(--foreground)" }}>
          <span className="current-time">{formatTime(currentTime)}</span>
          <span className="duration">{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

export default StereoAudioVisualizer;
